import { Box, Step, StepLabel, Stepper, styled } from "@mui/material";
import React from "react";
import {
    WorkflowLoading,
    WorkflowReviewStore,
} from "../Stores/WorkflowReviewStore";
import { useStore } from "utils/useStore";
import theme from "Theme/AppTheme";
import { Observer, observer } from "mobx-react";

const StepperContainer = styled(Box)({
    margin: theme.spacing(2, 8, 2, 8),
    width: "calc(100% - 128px)",
    justifyContent: "center",
    display: "flex",
});

const StyledStepper = styled(Stepper)({
    display: "flex",
    justifyContent: "space-between",
    padding: 0,
    background: "none",
    margin: theme.spacing(5, 0, 3, 0),
});

interface StyledStepProps {
    isActive: boolean;
    isDisabled: boolean;
    isHoverable: boolean;
    isFirst: boolean;
    isLast: boolean;
}

const StyledStep = styled(Step, {
    shouldForwardProp: (prop) =>
        prop !== "isActive" &&
        prop !== "isDisabled" &&
        prop !== "isHoverable" &&
        prop !== "isFirst" &&
        prop !== "isLast",
})<StyledStepProps>(
    ({ theme, isActive, isDisabled, isHoverable, isFirst, isLast }) => ({
        width: "172px",
        height: "38px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingLeft: theme.spacing(0),
        backgroundColor: isActive
            ? "white"
            : isDisabled
            ? "#ECEDF0"
            : theme.palette.gray[200],
        transition: "0.3s",
        position: "relative",
        borderRadius: isFirst
            ? "19px 0 0 19px"
            : isLast
            ? "0 19px 19px 0"
            : "0",
        "&:hover": isHoverable && {
            backgroundColor: theme.palette.gray[300],
        },
        "&::before":
            isActive && !isLast
                ? {
                      content: '""',
                      position: "absolute",
                      right: "-13.5px",
                      top: "50%",
                      transform: "translateY(-50%) rotate(45deg)",
                      width: "27px",
                      height: "27px",
                      backgroundColor: "white",
                      zIndex: 1,
                  }
                : {},
    }),
);

const StepTextContainer = styled(StepLabel)({
    zIndex: 10,
    cursor: "default",
    color: "red",
    textOverflow: "ellipsis",
    height: "38px",
});

const StepText = styled("div")({
    height: "30px",
    overflow: "hidden",
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(1),
    lineHeight: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

const WFRStepper = observer(() => {
    const store = useStore(WorkflowReviewStore);

    const getClassListForLevel = (idx: number): StyledStepProps => {
        const length = store.workflowReviewerInfo?.levelNames.length ?? -1;
        const levelCount =
            store.workflowReviewerInfo?.workflowInfo.workflowLevelInfos
                .length ?? 0;

        const isActive = idx === store.stepperLevel;
        const isDisabled = idx >= levelCount;
        const isHoverable =
            idx !== store.stepperLevel &&
            idx <
                (store.workflowReviewerInfo?.workflowInfo.workflowLevelInfos
                    .length ?? -1);
        const isFirst = idx === 0;
        const isLast = idx === length - 1;

        return { isActive, isDisabled, isHoverable, isFirst, isLast };
    };

    if (!store.workflowReviewerInfo || store.getTaskLoadingV2(WorkflowLoading))
        return null;

    return (
        <StepperContainer>
            <StyledStepper activeStep={store.stepperLevel}>
                <Observer>
                    {() => (
                        <>
                            {store.workflowReviewerInfo?.levelNames.map(
                                (name, idx) => {
                                    const classList = getClassListForLevel(idx);
                                    return (
                                        <StyledStep
                                            key={`workflow-step-${name}-${idx}`}
                                            {...classList}
                                            onClick={() => {
                                                if (
                                                    store.workflowReviewerInfo
                                                        ?.workflowInfo
                                                        .workflowLevelInfos[idx]
                                                ) {
                                                    store.setStepperLevel(idx);
                                                }
                                            }}
                                        >
                                            <StepTextContainer>
                                                <StepText>
                                                    {!!name
                                                        ? name
                                                        : `Level ${idx + 1}`}
                                                </StepText>
                                            </StepTextContainer>
                                        </StyledStep>
                                    );
                                },
                            )}
                        </>
                    )}
                </Observer>
            </StyledStepper>
        </StepperContainer>
    );
});

export default WFRStepper;
